import { isDev } from '@/lib/utils';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useRef } from 'react';
import type { AnalyticsEventPayload, AnalyticsEventType } from '../types/analytics-events';

type EventsWithPayload = {
    [K in AnalyticsEventType]: AnalyticsEventPayload<K> extends object ? K : never;
}[AnalyticsEventType];

type EventsWithoutPayload = {
    [K in AnalyticsEventType]: AnalyticsEventPayload<K> extends object ? never : K;
}[AnalyticsEventType];

interface SendEventFn {
    <T extends EventsWithPayload>(event: T, payload: AnalyticsEventPayload<T>): void;
    <T extends EventsWithoutPayload>(event: T): void;
}

export function useAnalytics(): SendEventFn {
    const postHog = usePostHog();
    const postHogRef = useRef(postHog);

    return useCallback((event, payload) => {
        const postHog = postHogRef.current;

        if (isDev()) {
            if (payload) {
                console.groupCollapsed(
                    `%c📊 Event: ${event}%c`,
                    'color: white; background: #3b3b98; padding: 4px 8px; border-radius: 4px;',
                    'color: #f8a5c2; font-weight: bold;',
                );
                console.log(`%c ${JSON.stringify(payload, null, 2)}`, 'color: #f78fb3; font-weight: bold;');
                console.groupEnd();
            } else {
                console.log(
                    `%c📊 Event: ${event}%c`,
                    'color: white; background: #3b3b98; padding: 4px 8px; border-radius: 4px;',
                    'color: #f8a5c2; font-weight: bold;',
                );
            }
        }

        postHog.capture(event, payload);
    }, []) as SendEventFn;
}
